import { getAppScale } from "../utils/appScale";
import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { libraryReadingMedallionById } from "../constants/libraryReadingMedallions";
import {
  LIBRARY_READING_TIMER_EVENT,
  readLibraryReadingTimer,
  writeLibraryReadingTimerRemaining,
} from "../utils/libraryReadingTimer";
import "./LibraryReadingTimer.css";

const MINUTE_RADIUS = 45;
const SECOND_RADIUS = 37;
const MINUTE_LENGTH = 2 * Math.PI * MINUTE_RADIUS;
const SECOND_LENGTH = 2 * Math.PI * SECOND_RADIUS;

function dash(fraction, length) {
  const shown = Math.max(0, Math.min(1, fraction)) * length;
  return `${shown.toFixed(1)} ${length.toFixed(1)}`;
}

const TIMER_SIZE = Math.round(64 * 1.3);
const TIMER_GROWTH = 15;
const CONTROL_SIZE = 28;
const CONTROL_GAP = 10;
function haloRayPath(count, turn) {
  const parts = [];
  for (let index = 0; index < count; index += 1) {
    const mid = ((index + turn) / count) * Math.PI * 2 - Math.PI / 2;
    const spread = ((Math.PI * 2) / count) * 0.38;
    const radius = 48;
    const x1 = 50 + Math.cos(mid - spread) * radius;
    const y1 = 50 + Math.sin(mid - spread) * radius;
    const x2 = 50 + Math.cos(mid + spread) * radius;
    const y2 = 50 + Math.sin(mid + spread) * radius;
    parts.push(
      `M50 50L${x1.toFixed(2)} ${y1.toFixed(2)}L${x2.toFixed(2)} ${y2.toFixed(2)}Z`
    );
  }
  return parts.join("");
}

const HALO_RAYS = haloRayPath(16, 0);
const HALO_RAYS_SOFT = haloRayPath(16, 0.5);

function ReadingClock({
  minutesShown,
  secondsLeftInMinute,
  totalMinutes,
  minuteColor,
  secondColor,
  showSeconds,
}) {
  return (
    <>
      <svg className="library-reading-timer__svg" viewBox="0 0 100 100" aria-hidden="true">
        <circle className="library-reading-timer__track" cx="50" cy="50" r={MINUTE_RADIUS} />
        <circle
          className="library-reading-timer__track library-reading-timer__track--seconds"
          cx="50"
          cy="50"
          r={SECOND_RADIUS}
        />
        <path
          className="library-reading-timer__remaining"
          strokeDasharray={dash(minutesShown / totalMinutes, MINUTE_LENGTH)}
          style={{ stroke: minuteColor }}
          d="M 50, 50 m -45, 0 a 45,45 0 1,0 90,0 a 45,45 0 1,0 -90,0"
        />
        <path
          className="library-reading-timer__remaining library-reading-timer__remaining--seconds"
          strokeDasharray={dash(secondsLeftInMinute / 60, SECOND_LENGTH)}
          style={{
            stroke: secondColor,
            transition: secondsLeftInMinute === 60 ? "none" : undefined,
          }}
          d="M 50, 50 m -37, 0 a 37,37 0 1,0 74,0 a 37,37 0 1,0 -74,0"
        />
      </svg>
      <span className="library-reading-timer__label">
        {showSeconds ? secondsLeftInMinute : minutesShown}
      </span>
    </>
  );
}

function MedallionMark({ Icon, offset, size }) {
  return (
    <Icon
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      style={{
        left: `${offset}px`,
        top: `${offset}px`,
        width: `${size}px`,
        height: `${size}px`,
      }}
    />
  );
}

function QuietClock({
  minutesShown,
  secondsLeftInMinute,
  totalMinutes,
  minuteColor,
  secondColor,
}) {
  return (
    <ReadingClock
      minutesShown={minutesShown}
      secondsLeftInMinute={secondsLeftInMinute}
      totalMinutes={totalMinutes}
      minuteColor={minuteColor}
      secondColor={secondColor}
      showSeconds={false}
    />
  );
}

function DiscreteTimerFace({
  turn,
  turnPhase,
  revealed,
  onTurnEnd,
  turnGen,
  medalOffset,
  medalSize,
  MedallionIcon,
  minutesShown,
  secondsLeftInMinute,
  totalMinutes,
  minuteColor,
  secondColor,
}) {
  const clock = (
    <QuietClock
      minutesShown={minutesShown}
      secondsLeftInMinute={secondsLeftInMinute}
      totalMinutes={totalMinutes}
      minuteColor={minuteColor}
      secondColor={secondColor}
    />
  );
  const medallion = (
    <div className="library-reading-timer__face library-reading-timer__face--medallion">
      <MedallionMark Icon={MedallionIcon} offset={medalOffset} size={medalSize} />
    </div>
  );
  if (!turn) return revealed ? clock : medallion;
  const showMedal = turn === "to-clock" ? turnPhase === "out" : turnPhase === "in";
  return (
    <div
      className={`library-reading-timer__turn ${
        turnPhase === "in" ? "is-in" : "is-out"
      }`}
    >
      <div className="library-reading-timer__rotor" data-gen={turnGen} onAnimationEnd={onTurnEnd}>
        {showMedal ? medallion : clock}
      </div>
    </div>
  );
}

export default function LibraryReadingTimer({ pending = false }) {
  const initial = readLibraryReadingTimer();
  const [preference, setPreference] = useState(initial);
  const [remaining, setRemaining] = useState(initial.remainingSeconds);
  const [paused, setPaused] = useState(initial.paused);
  const [place, setPlace] = useState(null);
  const [showSeconds, setShowSeconds] = useState(false);
  const [overMedal, setOverMedal] = useState(false);
  const [haloSeen, setHaloSeen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [turn, setTurn] = useState(null);
  const [turnPhase, setTurnPhase] = useState("out");
  const rootRef = useRef(null);
  const revealedRef = useRef(false);
  const turnRef = useRef(null);
  const turnPhaseRef = useRef("out");
  const turnGenRef = useRef(0);
  const flipHoldRef = useRef(0);
  const completeTurnRef = useRef(() => {});
  revealedRef.current = revealed;
  completeTurnRef.current = () => {
    const dest = turnRef.current;
    if (!dest) return;
    turnRef.current = null;
    turnGenRef.current += 1;
    window.clearTimeout(flipHoldRef.current);
    if (dest === "to-clock") {
      revealedRef.current = true;
      setRevealed(true);
    } else {
      revealedRef.current = false;
      setRevealed(false);
    }
    setTurn(null);
  };
  const remainingRef = useRef(remaining);
  const pausedRef = useRef(paused);
  const haloSeenRef = useRef(false);
  const previousRemainingRef = useRef(remaining);
  remainingRef.current = remaining;
  pausedRef.current = paused;

  useEffect(() => {
    const onChange = (event) => {
      const next = event.detail || readLibraryReadingTimer();
      setPreference(next);
      setRemaining(next.remainingSeconds);
      setPaused(Boolean(next.paused));
    };
    window.addEventListener(LIBRARY_READING_TIMER_EVENT, onChange);
    return () => window.removeEventListener(LIBRARY_READING_TIMER_EVENT, onChange);
  }, []);

  useEffect(() => {
    writeLibraryReadingTimerRemaining(remaining, paused);
  }, [remaining, paused]);

  useEffect(() => {
    return () => {
      writeLibraryReadingTimerRemaining(remainingRef.current, pausedRef.current);
    };
  }, []);

  useEffect(() => {
    if (remaining > previousRemainingRef.current) {
      haloSeenRef.current = false;
      setHaloSeen(false);
    }
    previousRemainingRef.current = remaining;
  }, [remaining]);

  useEffect(() => {
    if (!preference.enabled || paused || pending || remaining <= 0) return undefined;
    const id = window.setInterval(() => {
      setRemaining((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [preference.enabled, preference.minutes, paused, pending, remaining <= 0]);

  useEffect(() => {
    if (!preference.enabled) return undefined;

    const startTurn = (next) => {
      if (turnRef.current === next) return;
      if (next === "to-clock" && revealedRef.current && !turnRef.current) return;
      if (next === "to-medal" && !revealedRef.current && !turnRef.current) return;
      const gen = turnGenRef.current + 1;
      turnGenRef.current = gen;
      turnRef.current = next;
      turnPhaseRef.current = "out";
      setTurnPhase("out");
      setTurn(next);
      window.clearTimeout(flipHoldRef.current);
      const flipMs = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 40 : 760;
      flipHoldRef.current = window.setTimeout(() => {
        if (turnGenRef.current === gen) completeTurnRef.current();
      }, flipMs);
    };

    const syncHover = (event) => {
      const element = rootRef.current;
      if (!element) {
        setShowSeconds(false);
        return;
      }
      const rect = element.getBoundingClientRect();
      const radius = rect.width / 2;
      if (radius < 1) return;
      const dx = event.clientX - (rect.left + radius);
      const dy = event.clientY - (rect.top + rect.height / 2);
      const inside = dx * dx + dy * dy <= radius * radius;
      setOverMedal((current) => (current === inside ? current : inside));
      if (
        inside &&
        preference.discrete &&
        remainingRef.current <= 0
      ) {
        haloSeenRef.current = true;
        setHaloSeen(true);
      }
      if (preference.discrete) {
        setShowSeconds(false);
        startTurn(inside ? "to-clock" : "to-medal");
        return;
      }
      setShowSeconds((current) => (current === inside ? current : inside));
    };

    const clearHover = () => {
      setShowSeconds(false);
      setOverMedal(false);
      if (!preference.discrete) return;
      startTurn("to-medal");
    };
    window.addEventListener("pointermove", syncHover);
    window.addEventListener("blur", clearHover);
    document.addEventListener("pointerleave", clearHover);
    return () => {
      window.removeEventListener("pointermove", syncHover);
      window.removeEventListener("blur", clearHover);
      document.removeEventListener("pointerleave", clearHover);
    };
  }, [preference.enabled, preference.discrete]);

  useEffect(() => () => window.clearTimeout(flipHoldRef.current), []);

  useEffect(() => {
    if (preference.discrete) return undefined;
    turnRef.current = null;
    revealedRef.current = false;
    window.clearTimeout(flipHoldRef.current);
    setTurn(null);
    setRevealed(false);
    return undefined;
  }, [preference.discrete]);

  useEffect(() => {
    if (!preference.enabled) {
      setPlace(null);
      return undefined;
    }

    const measure = () => {
      const frame = document.querySelector(".custom-pattern");
      const total = document.querySelector("[data-library-page-total]");
      const home = document.querySelector("[data-library-home]");
      if (!frame || !total || !home) {
        setPlace(null);
        return;
      }
      const scale = getAppScale() || 1;
      const frameRect = frame.getBoundingClientRect();
      const totalRect = total.getBoundingClientRect();
      const homeRect = home.getBoundingClientRect();
      const gapLeft = (totalRect.right - frameRect.left) / scale;
      const gapRight = (homeRect.left - frameRect.left) / scale;
      const gap = gapRight - gapLeft;
      if (gap < 28) {
        setPlace(null);
        return;
      }
      const size = Math.min(TIMER_SIZE, Math.max(Math.round(40 * 1.3), gap - 8));
      const top =
        ((totalRect.top + totalRect.height / 2 + homeRect.top + homeRect.height / 2) / 2 -
          frameRect.top) /
          scale +
        15;
      const next = {
        left: (gapLeft + gapRight) / 2,
        top,
        size,
      };
      setPlace((current) => {
        if (
          current &&
          Math.abs(current.left - next.left) < 0.5 &&
          Math.abs(current.top - next.top) < 0.5 &&
          Math.abs(current.size - next.size) < 0.5
        ) {
          return current;
        }
        return next;
      });
    };

    measure();
    const frame = document.querySelector(".custom-pattern");
    const total = document.querySelector("[data-library-page-total]");
    const home = document.querySelector("[data-library-home]");
    const observer = new ResizeObserver(measure);
    if (frame) observer.observe(frame);
    if (total) observer.observe(total);
    if (home) observer.observe(home);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [preference.enabled, pending]);

  if (!preference.enabled || !place) return null;

  const minutesShown = remaining <= 0 ? 0 : Math.ceil(remaining / 60);
  const secondsLeftInMinute =
    remaining > 0 && remaining % 60 === 0 ? 60 : remaining % 60;
  const discrete = Boolean(preference.discrete);
  const secondsVisible = showSeconds && !discrete;
  const timeUp = remaining <= 0;
  const haloVisible =
    discrete &&
    timeUp &&
    !haloSeen &&
    !overMedal &&
    !revealed &&
    turn !== "to-clock";
  const medallion = libraryReadingMedallionById(preference.medallionId);
  const MedallionIcon = medallion.Icon;
  const drawSize = place.size + TIMER_GROWTH;
  const medalSize = Math.round(drawSize * 0.97);
  const medalOffset = Math.round((drawSize - medalSize) / 2);
  const pauseCenter = place.left - place.size / 2 - CONTROL_GAP - CONTROL_SIZE / 2;
  const resetCenter = place.left + place.size / 2 + CONTROL_GAP + CONTROL_SIZE / 2;

  if (pending) {
    return (
      <>
        <span
          className="register-skel library-reading-timer__skel"
          style={{
            left: `${pauseCenter}px`,
            top: `${place.top}px`,
            width: `${CONTROL_SIZE}px`,
            height: `${CONTROL_SIZE}px`,
          }}
        />
        <span
          className="register-skel library-reading-timer__skel"
          style={{
            left: `${place.left}px`,
            top: `${place.top}px`,
            width: `${drawSize}px`,
            height: `${drawSize}px`,
          }}
        />
        <span
          className="register-skel library-reading-timer__skel"
          style={{
            left: `${resetCenter}px`,
            top: `${place.top}px`,
            width: `${CONTROL_SIZE}px`,
            height: `${CONTROL_SIZE}px`,
          }}
        />
      </>
    );
  }

  const togglePaused = () => setPaused((current) => !current);
  const restart = () => {
    setPaused(false);
    setRemaining(preference.minutes * 60);
  };

  return (
    <>
      <button
        type="button"
        className="library-reading-timer__control"
        style={{ left: `${pauseCenter}px`, top: `${place.top}px` }}
        aria-label={paused ? "Reprendre le minuteur" : "Mettre le minuteur en pause"}
        onClick={togglePaused}
      >
        {paused ? (
          <Play size={14} strokeWidth={2} aria-hidden="true" />
        ) : (
          <Pause size={14} strokeWidth={2} aria-hidden="true" />
        )}
      </button>
      <div
        ref={rootRef}
        className={`library-reading-timer${turn ? " is-turning" : ""}`}
        style={{
          left: `${place.left}px`,
          top: `${place.top}px`,
          width: `${drawSize}px`,
          height: `${drawSize}px`,
          "--library-timer-size": `${drawSize}px`,
        }}
        aria-label={
          secondsVisible
            ? `Minuteur de lecture : ${secondsLeftInMinute} seconde${
                secondsLeftInMinute > 1 ? "s" : ""
              }`
            : `Minuteur de lecture : ${minutesShown} minute${
                minutesShown > 1 ? "s" : ""
              } restante${minutesShown > 1 ? "s" : ""}`
        }
        role="timer"
      >
        {haloVisible ? (
          <div className="library-reading-timer__halo" aria-hidden="true">
            <div className="library-reading-timer__halo-pulse">
            <svg className="library-reading-timer__halo-rays" viewBox="0 0 100 100" aria-hidden="true">
              <defs>
                <radialGradient id="library-timer-halo-grad" cx="50" cy="50" r="48" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#f6e2a4" stopOpacity="0.9" />
                  <stop offset="52%" stopColor="#f3d48a" stopOpacity="0.85" />
                  <stop offset="68%" stopColor="#f0b06a" stopOpacity="0.6" />
                  <stop offset="84%" stopColor="#e08a72" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="#d07068" stopOpacity="0" />
                </radialGradient>
              </defs>
              <path fill="url(#library-timer-halo-grad)" d={HALO_RAYS} />
            </svg>
            <svg
              className="library-reading-timer__halo-rays library-reading-timer__halo-rays--soft"
              viewBox="0 0 100 100"
              aria-hidden="true"
            >
              <defs>
                <radialGradient id="library-timer-halo-grad-soft" cx="50" cy="50" r="48" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#f8e7b8" stopOpacity="0.8" />
                  <stop offset="56%" stopColor="#f2c98a" stopOpacity="0.55" />
                  <stop offset="76%" stopColor="#e8a07a" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#d08070" stopOpacity="0" />
                </radialGradient>
              </defs>
              <path fill="url(#library-timer-halo-grad-soft)" d={HALO_RAYS_SOFT} />
            </svg>
            </div>
          </div>
        ) : null}
        {discrete ? (
          <DiscreteTimerFace
            turn={turn}
            revealed={revealed}
            onTurnEnd={(event) => {
              if (event.target !== event.currentTarget) return;
              if (!event.animationName.startsWith("library-timer-edge")) return;
              if (Number(event.currentTarget.dataset.gen) !== turnGenRef.current) return;
              if (turnPhaseRef.current === "out") {
                turnPhaseRef.current = "in";
                setTurnPhase("in");
                return;
              }
              completeTurnRef.current();
            }}
            turnPhase={turnPhase}
            turnGen={turnGenRef.current}
            medalOffset={medalOffset}
            medalSize={medalSize}
            MedallionIcon={MedallionIcon}
            minutesShown={minutesShown}
            secondsLeftInMinute={secondsLeftInMinute}
            totalMinutes={preference.minutes}
            minuteColor={preference.minuteColor}
            secondColor={preference.secondColor}
          />
        ) : (
          <ReadingClock
            minutesShown={minutesShown}
            secondsLeftInMinute={secondsLeftInMinute}
            totalMinutes={preference.minutes}
            minuteColor={preference.minuteColor}
            secondColor={preference.secondColor}
            showSeconds={secondsVisible}
          />
        )}
        <span className="library-reading-timer__live" aria-live="polite">
          {minutesShown} minute{minutesShown > 1 ? "s" : ""}
        </span>
      </div>
      <button
        type="button"
        className="library-reading-timer__control"
        style={{ left: `${resetCenter}px`, top: `${place.top}px` }}
        aria-label="Réinitialiser le minuteur"
        onClick={restart}
      >
        <RotateCcw size={15} strokeWidth={2} aria-hidden="true" />
      </button>
    </>
  );
}
